import { useCallback, useRef } from "react";
import { API } from "../helpers/API/amplifyApi";
import {
  osName,
  osVersion,
  browserName,
  browserVersion,
  deviceType,
} from "react-device-detect";
import { v4 as uuid } from "uuid";
import { requestData } from "../helpers/API/RequestFactory";
import { detectLocalIp } from "../utils/localIp";
import {
  currentDeviceKind,
  devicePolicyErrorMessage,
  isDeviceAccessAllowed,
} from "../utils/devicePolicy";

const MAX_ACTIVE_SESSIONS = 8;
const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function readStoredSessionId() {
  try {
    const raw = window.localStorage.getItem("sessionId");
    if (!raw || raw === "null") return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === "string" && parsed ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredSessionId(sessionId) {
  if (!sessionId) {
    window.localStorage.setItem("sessionId", "null");
    return;
  }
  window.localStorage.setItem("sessionId", JSON.stringify(sessionId));
}

const updateSession = async (body, user) => {
  if (!user?.identityId) return;
  await API.put("users", `/users/${user.identityId}`, {
    body: { activeSessions: body },
  }).catch((err) => {
    console.error("Error updating activeSessions:", err);
  });
};

export const getActiveSessions = (user) => {
  const { activeSessions = [] } = user || {};
  return Array.isArray(activeSessions) ? activeSessions : [];
};

/** Drop kicked/stale sessions and cap list size (newest first). */
export function pruneActiveSessions(sessions = [], keepSessionId = null) {
  const now = Date.now();
  const cleaned = (Array.isArray(sessions) ? sessions : [])
    .filter((s) => s && s.sessionId && !s.logout)
    .filter((s) => {
      const t = Number(s.loggedIn || s.lastSeenAt || 0);
      if (!t) return true;
      return now - t <= SESSION_TTL_MS;
    });

  // Prefer keeping the current browser session even if older than others.
  const current = keepSessionId
    ? cleaned.find((s) => s.sessionId === keepSessionId)
    : null;
  const rest = cleaned
    .filter((s) => !keepSessionId || s.sessionId !== keepSessionId)
    .sort((a, b) => Number(b.loggedIn || 0) - Number(a.loggedIn || 0));

  const next = current ? [current, ...rest] : rest;
  // Dedupe by sessionId
  const seen = new Set();
  const unique = [];
  for (const s of next) {
    if (seen.has(s.sessionId)) continue;
    seen.add(s.sessionId);
    unique.push(s);
  }
  return unique.slice(0, MAX_ACTIVE_SESSIONS);
}

/**
 * Stable browser session helper.
 * Session id is stored in localStorage and reused across renders / reloads
 * so we never append hundreds of duplicate rows for the same tab.
 */
export const useUserSession = () => {
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = readStoredSessionId() || uuid();
  }

  const open = useCallback(async (user) => {
    const deviceKind = currentDeviceKind();
    const policy = user?.allowedDeviceTypes || "both";
    if (!isDeviceAccessAllowed(policy, deviceKind)) {
      const err = new Error(devicePolicyErrorMessage(policy));
      err.code = "DEVICE_POLICY";
      err.allowedDeviceTypes = policy;
      throw err;
    }

    const sessionId = sessionIdRef.current || readStoredSessionId() || uuid();
    sessionIdRef.current = sessionId;

    let ipv4 = null;
    try {
      ipv4 = (await requestData("getIp")).split(",")[0];
    } catch (_e) {
      ipv4 = null;
    }
    const localIp = await detectLocalIp(1200);

    const existing = getActiveSessions(user);
    const idx = existing.findIndex((s) => s?.sessionId === sessionId);
    const row = {
      sessionId,
      loggedIn:
        idx >= 0 && existing[idx]?.loggedIn
          ? existing[idx].loggedIn
          : Date.now(),
      lastSeenAt: Date.now(),
      osName,
      osVersion,
      browserName,
      browserVersion,
      deviceType: deviceType || deviceKind,
      ipv4,
      localIp,
      logout: false,
    };

    const merged =
      idx >= 0
        ? existing.map((s, i) => (i === idx ? { ...s, ...row } : s))
        : [...existing, row];

    const pruned = pruneActiveSessions(merged, sessionId);
    await updateSession(pruned, user);
    writeStoredSessionId(sessionId);
    return sessionId;
  }, []);

  const close = useCallback(async (user) => {
    const sessionId = readStoredSessionId() || sessionIdRef.current;
    const next = getActiveSessions(user).filter(
      (s) => s?.sessionId !== sessionId
    );
    await updateSession(pruneActiveSessions(next), user);
    sessionIdRef.current = null;
    writeStoredSessionId(null);
  }, []);

  const update = useCallback(async (newValues, user) => {
    const sessionId = readStoredSessionId() || sessionIdRef.current;
    if (!sessionId) return;
    const existing = getActiveSessions(user);
    const idx = existing.findIndex((s) => s?.sessionId === sessionId);
    const merged =
      idx >= 0
        ? existing.map((s, i) =>
            i === idx ? { ...s, ...newValues, sessionId, lastSeenAt: Date.now() } : s
          )
        : [
            ...existing,
            { sessionId, loggedIn: Date.now(), ...newValues, lastSeenAt: Date.now() },
          ];
    await updateSession(pruneActiveSessions(merged, sessionId), user);
  }, []);

  return { open, close, update };
};
